import { TaskStatus } from "./../types";
import _first from "lodash/first";
import _pick from "lodash/pick";
import _path from "lodash/fp/path";
import _mapValues from "lodash/mapValues";
import {
  TypeStatusMap,
  StatusMap,
  DynamicForkRelations,
} from "./StatusMapTypes";
import {
  TaskDef,
  Execution,
  ExecutionTask,
  TaskType,
  ExecutedData,
} from "./../types";
import {
  TaskDefExecutionContext,
  WorkflowDefExecutionContext,
} from "types/WorkflowExecution";

const MAX_EXPAND_TASKS = 2;

export const relatedNamesToTaskDef = (
  names: string[],
  executionTasks: ExecutionTask[],
  parentTaskReferenceName: string
) => {
  const relationTasks = names.map((tn) =>
    executionTasks.find((t) => t.workflowTask.taskReferenceName === tn)
  );
  const taskWithSiblings = names.reduce(
    (tnAcc, tn) => ({
      ...tnAcc,
      [tn]: {
        siblings: relationTasks,
        parentTaskReferenceName,
      },
    }),
    {}
  );
  return taskWithSiblings;
};

type StatusMapTupleAcumulator = [
  StatusMap,
  Record<string, DynamicForkRelations>
];
export const executionTasksToStatusMap = (executionTasks: ExecutionTask[]) => {
  const [statusMap] = executionTasks.reduce(
    (
      [acc, related]: StatusMapTupleAcumulator,
      task: ExecutionTask
    ): StatusMapTupleAcumulator => {
      const loopOver = acc[task.workflowTask.taskReferenceName]?.loopOver || [];
      let newRelated = related;
      if (task.workflowTask.type === TaskType.FORK_JOIN_DYNAMIC) {
        newRelated = {
          ...related,
          ...relatedNamesToTaskDef(
            task.inputData?.forkedTasks || [],
            executionTasks,
            task.workflowTask.taskReferenceName
          ),
        };
      }
      return [
        {
          ...acc,
          [task.workflowTask.taskReferenceName]: {
            ...task,
            loopOver: loopOver.concat(task),
            related: related[task.workflowTask.taskReferenceName],
          },
        } as Record<string, TypeStatusMap>,
        newRelated,
      ];
    },
    [{}, {}]
  );
  return statusMap;
};

const extractFirstTaskReferenceName = (tasks: TaskDef[]) => {
  const firstTask = _first(tasks);
  return firstTask == null ? "no_task" : firstTask.taskReferenceName;
};

const entireCollapsedStatus = (
  executionTask: TypeStatusMap,
  firstTaskExecutionDataRaw: Pick<
    TypeStatusMap,
    "status" | "executed" | "loopOver"
  >,
  statusMap: StatusMap,
  returnStatusArray = false
) => {
  const forkedTaskDefs: TaskDef[] =
    _path("inputData.forkedTaskDefs", executionTask) ?? [];
  const collapsedTaskRefNames = forkedTaskDefs
    ? forkedTaskDefs.map((item) => item.taskReferenceName)
    : [];
  const collapsedTasksStatus =
    collapsedTaskRefNames.map((item) => statusMap[item]?.status) ?? [];
  if (returnStatusArray) {
    return collapsedTasksStatus;
  }
  if (collapsedTasksStatus.includes(TaskStatus.FAILED)) {
    return TaskStatus.FAILED;
  } else if (collapsedTasksStatus.includes(TaskStatus.TIMED_OUT)) {
    return TaskStatus.TIMED_OUT;
  } else if (collapsedTasksStatus.includes(TaskStatus.SKIPPED)) {
    return TaskStatus.SKIPPED;
  } else if (collapsedTasksStatus.includes(TaskStatus.CANCELED)) {
    return TaskStatus.CANCELED;
  } else {
    return firstTaskExecutionDataRaw.status;
  }
};

export const taskStatusUpdater = (
  tasks: TaskDef[] = [],
  statusMap: StatusMap,
  expandDynamic: string[]
): TaskDefExecutionContext[] => {
  return tasks.map((task) => {
    const { type, taskReferenceName } = task;
    const executionTask = statusMap[taskReferenceName];

    const executionDataRaw = _pick(
      executionTask || {
        status: TaskStatus.PENDING,
        executed: false,
        loopOver: [],
      },
      ["status", "executed", "loopOver"]
    );
    const executionData: ExecutedData = {
      status: executionDataRaw.status as TaskStatus,
      executed: executionDataRaw.executed,
      attempts: executionDataRaw.loopOver.length,
      outputData: executionTask?.outputData,
    };

    if (type === TaskType.FORK_JOIN) {
      const forkTasks: Array<TaskDefExecutionContext[]> = (
        task.forkTasks || []
      ).map((taa) => taskStatusUpdater(taa, statusMap, expandDynamic));
      return {
        ...task,
        forkTasks,
        executionData,
      } as TaskDefExecutionContext;
    } else if (type === TaskType.DECISION || type === TaskType.SWITCH) {
      const decisionCases: Record<string, TaskDefExecutionContext[]> =
        _mapValues(task.decisionCases, (decisionTasks: TaskDef[]) =>
          taskStatusUpdater(decisionTasks, statusMap, expandDynamic)
        );
      return {
        ...task,
        decisionCases,
        defaultCase: taskStatusUpdater(
          task.defaultCase!,
          statusMap,
          expandDynamic
        ),
        executionData,
      } as TaskDefExecutionContext;
    } else if (type === TaskType.DO_WHILE) {
      return {
        ...task,
        loopOver: taskStatusUpdater(task.loopOver!, statusMap, expandDynamic),
        executionData,
      } as TaskDefExecutionContext;
    } else if (
      type === TaskType.FORK_JOIN_DYNAMIC &&
      executionData.status !== TaskStatus.PENDING
    ) {
      const doesNotRequireCollapse =
        (executionTask!.inputData?.forkedTaskDefs || []).length <
        MAX_EXPAND_TASKS;

      if (expandDynamic.includes(taskReferenceName) || doesNotRequireCollapse) {
        const forkTasks: Array<TaskDefExecutionContext[]> = taskStatusUpdater(
          executionTask!.inputData!.forkedTaskDefs,
          statusMap,
          expandDynamic
        ).map((t: TaskDefExecutionContext) => [t]);
        return {
          ...task,
          forkTasks,
          joinOn: executionTask?.inputData?.forkedTasks,
          executionData: {
            ...executionData,
            ...(doesNotRequireCollapse ? {} : { collapsed: false }),
          },
        } as TaskDefExecutionContext;
      }

      const firstTaskReferenceName = extractFirstTaskReferenceName(
        executionTask!.inputData!.forkedTaskDefs
      );

      const firstTaskExecutionDataRaw = _pick(
        statusMap[firstTaskReferenceName] || {
          status: TaskStatus.PENDING,
          executed: false,
        },
        ["status", "executed", "loopOver"]
      );

      const firstTaskExecutionData = {
        status: entireCollapsedStatus(
          executionTask,
          firstTaskExecutionDataRaw,
          statusMap
        ),
        executed: firstTaskExecutionDataRaw.executed,
        attempts: firstTaskExecutionDataRaw.loopOver.length,
      };
      return {
        ...task,
        forkTasks: [
          [
            {
              type: "FORK_JOIN_COLLAPSED" as TaskType,
              taskReferenceName: firstTaskReferenceName,
              executionData: {
                ...firstTaskExecutionData,
                collapsedTasks: executionTask?.inputData?.forkedTaskDefs,
                parentTaskReferenceName: task.taskReferenceName,
                collapsedTasksStatus: entireCollapsedStatus(
                  executionTask,
                  firstTaskExecutionDataRaw,
                  statusMap,
                  true
                ),
              },
            },
          ],
        ],
        executionData: {
          ...executionData,
          collapsed: true,
        },
      } as TaskDefExecutionContext;
    } else if (type === TaskType.SIMPLE) {
      return {
        ...task,
        executionData: {
          ...executionData,
          domain: executionTask?.domain,
        },
      } as TaskDefExecutionContext;
    } else if (type === TaskType.TASK_SUMMARY) {
      return {
        ...task,
        executionData: {
          ...executionData,
          summary: executionTask?.outputData?.summary,
        },
      } as TaskDefExecutionContext;
    }
    return {
      ...task,
      executionData,
    } as TaskDefExecutionContext;
  });
};

export const executionToWorkflowDef = (
  execution: Execution,
  expandDynamic: string[] = []
): [WorkflowDefExecutionContext, StatusMap] => {
  const taskExecutionMap = executionTasksToStatusMap(execution.tasks);
  return [
    {
      ...execution.workflowDefinition,
      tasks: taskStatusUpdater(
        execution.workflowDefinition.tasks,
        taskExecutionMap,
        expandDynamic
      ),
    },
    taskExecutionMap,
  ];
};
